"use client";

import React, { useState, useEffect } from "react";
import BaseTailoringQuestions, { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Button } from "../ui/button";
import { FileSignature } from "lucide-react";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface WrittenRepresentationsLandingProps {
  onSelect?: (section: string) => void;
}

const WR_PROCEDURES_IF_NO_2 =
  "Have we performed the following procedures:\n• Discuss the matter with management;\n• Re-evaluate the integrity of management and evaluate the effect that this may have on the reliability of representations (oral or written) and audit evidence in general; and\n• Take appropriate actions, including determining the possible effect on the opinion in the auditor's report.";

const WR_PROCEDURES_IF_YES_3 =
  "Have we performed the following:\n• Determine the effect that such concerns may have on the reliability of representations (oral or written) and audit evidence in general.\n• Perform audit procedures to attempt to resolve the matter. If the matter remains unresolved, the auditor shall reconsider the assessment of the competence, integrity, ethical values or diligence of management, or of its commitment to or enforcement of these, and shall determine the effect that this may have on the reliability of representations (oral or written) and audit evidence in general.";

const tailoringQuestions: TailoringQuestion[] = [
  {
    id: "WR-1",
    question:
      "Have we obtained written representations from management and where appropriate, those charged with governance?",
    type: "radio",
    options: ["Yes", "No"],
  },
  {
    id: "WR-2",
    question: "Has management provided all requested written representations?",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      condition: "No",
      id: "WR-2-document",
      question: WR_PROCEDURES_IF_NO_2 + "\n\nPlease document:",
      type: "document",
    },
  },
  {
    id: "WR-3",
    question:
      "Are there any concerns regarding reliability of the written representation including:\ni. Concerns about competence, integrity, ethical values, or diligence of management, or about its commitment to or enforcement of these, or\nii. Inconsistency with other audit evidence?",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      condition: "Yes",
      id: "WR-3-document",
      question: WR_PROCEDURES_IF_YES_3 + "\n\nPlease document:",
      type: "document",
    },
  },
  {
    id: "WR-4",
    question:
      "Does the written representation cover all financial statements and period(s) referred to in the auditor's report?",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      condition: "No",
      id: "WR-4-document",
      question: "Please document:",
      type: "document",
    },
  },
  {
    id: "WR-5",
    question:
      "Do the signed written representations satisfy the following requirements?\ni. Signed by individuals with appropriate responsibilities for the financial statements and knowledge of the matters concerned\nii. Dated as near as practicable to, but not after, the date of the auditor's report on the financial statements.\niii. Covers all the financial statements and the periods referred to in the auditor's opinion\niv. Management has provided all requested representations\nv. There are no concerns on the reliability of the written representations.",
    type: "radio",
    options: ["Yes", "No"],
    follow_up: {
      condition: "always",
      id: "WR-5-document",
      question: "Please document:",
      type: "document",
    },
  },
];

const WORKSPACE_NAME = "Obtain Written Representations";

export default function WrittenRepresentationsLanding(_props: WrittenRepresentationsLandingProps) {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const m = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(m?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    load();
  }, [currentEngagement, user]);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="WR"
        title="Obtain Written Representations"
        description="Answer the following questions based on written representations requirements."
        questions={tailoringQuestions}
      />
    </div>
  );
}
